import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SignupPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success(`Welcome to Nexfy, ${data.name.split(" ")[0]} ✦`);
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "mb-4 w-full rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-white placeholder-slate-500 outline-none focus:border-violet-400 transition-colors";

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0F19] flex items-center justify-center px-4">
      <div className="blob absolute -top-24 -right-24 h-96 w-96 rounded-full bg-pink-600/25 blur-3xl" />
      <div className="blob absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-violet-600/30 blur-3xl" style={{ animationDelay: "-3s" }} />
      <div className="absolute inset-0 canvas-dots opacity-40" />
      <div className="relative w-full max-w-md card-pop">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 text-4xl font-extrabold tracking-tight text-white font-display">
            <Sparkles className="h-9 w-9 text-violet-400" /> Nexfy
          </div>
          <p className="mt-2 font-hand text-2xl text-pink-300">your workboard, your vibes ✦</p>
        </div>
        <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl shadow-2xl">
          <label className="mb-1 block text-xs font-mono uppercase tracking-wider text-slate-400">Name</label>
          <input data-testid="signup-name-input" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={field} />
          <label className="mb-1 block text-xs font-mono uppercase tracking-wider text-slate-400">Email</label>
          <input data-testid="signup-email-input" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@nexfy.in" className={field} />
          <label className="mb-1 block text-xs font-mono uppercase tracking-wider text-slate-400">Password</label>
          <input data-testid="signup-password-input" type="password" required minLength={6} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="6+ characters" className={field} />
          <button data-testid="signup-submit-button" type="submit" disabled={loading}
            className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-pink-500 py-3 font-bold text-white shadow-lg shadow-violet-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50">
            {loading ? "Creating your board..." : "Create my board →"}
          </button>
          <p className="mt-5 text-center text-sm text-slate-400">
            Already in? <Link to="/login" data-testid="signup-login-link" className="text-violet-300 hover:text-violet-200">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
